import { Button, Dialog, DialogBody, DialogContent, DialogFooter, IconButton, Text } from '@civilgrid/ui/components';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
  type AgencySubGeometry,
  type AgencySubGeometryMemberContact,
  type AgencySubGeometryMemberLink,
  addAgencyLink as apiAddAgencyLink,
  editAgencyLink as apiEditAgencyLink,
  newAgencyContact as apiNewAgencyContact,
  updateAgencyContact as apiUpdateAgencyContact,
  updateAgencySubGeometryNote as apiUpdateAgencySubGeometryNote,
} from '~/api/civilsearch';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { AgencyLinkType } from '~/common/types';
import ControlledCheckbox from '~/components2/forms/controlled/ControlledCheckbox';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import ControlledSelect from '~/components2/forms/controlled/ControlledSelect';
import Form from '~/components2/forms/Form';
import Select from '~/components2/forms/Select';
import { Notify } from '~/components2/Notify';
import { subGeometryDisplayName } from './helpers';
import { computeMemberDiff, deriveMemberDraftState, partitionMembers } from './manageCandidates';

// CG-482: the scoped dropdown already prevents cross-agency assignment, but the API enforces it too
// (400 with a `reason`). If that defensive guard ever fires, surface the server's reason verbatim.
function serverReason(e: unknown): string | undefined {
  return (e as { response?: { data?: { reason?: string } } })?.response?.data?.reason;
}

// ---- shared link-type items (mirror AddLink/common.tsx) ----
const linkTypeMap: { [key in AgencyLinkType]: string } = {
  [AgencyLinkType.Nda]: 'NDA',
  [AgencyLinkType.Letterhead]: 'Letterhead',
  [AgencyLinkType.Other]: 'Other',
};
const linkTypeItems = Object.values(AgencyLinkType).map((type) => ({
  value: type,
  label: <div className="text-xs">{linkTypeMap[type]}</div>,
}));

// ---- form value types ----
type TNewContactValues = {
  firstName: string;
  lastName: string;
  email: string;
  title: string;
};

type TNewLinkValues = {
  title: string;
  description: string;
  url: string;
  type: AgencyLinkType;
  isPrivate: boolean;
};

// ---- controlled dialog props (Part B) ----
interface Props {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Picks where focus lands on close; the popover or popup that opened the dialog is gone by then. */
  onCloseAutoFocus?: (event: Event) => void;
  agencyId: string;
  subGeometry: AgencySubGeometry;
  contacts: AgencySubGeometryMemberContact[];
  links: AgencySubGeometryMemberLink[];
  onChanged: () => void;
}

export default function ManageSubGeometryDialog({
  isOpen,
  onOpenChange,
  onCloseAutoFocus,
  agencyId,
  subGeometry,
  contacts,
  links,
  onChanged,
}: Props) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        title={`Manage: ${subGeometryDisplayName(subGeometry.name)}`}
        onCloseAutoFocus={onCloseAutoFocus}
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid={`agency-sub-geometries-manage-dialog-${subGeometry.id}`}
      >
        <ManageDialogBody
          agencyId={agencyId}
          subGeometry={subGeometry}
          contacts={contacts}
          links={links}
          onChanged={onChanged}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function ManageDialogBody({
  agencyId,
  subGeometry,
  contacts,
  links,
  onChanged,
  onClose,
}: {
  agencyId: string;
  subGeometry: AgencySubGeometry;
  contacts: AgencySubGeometryMemberContact[];
  links: AgencySubGeometryMemberLink[];
  onChanged: () => void;
  onClose: () => void;
}) {
  // Server truth: which members the API currently has attached to this sub region.
  const { candidates: candidateContacts } = partitionMembers(contacts, subGeometry.id);
  const { candidates: candidateLinks } = partitionMembers(links, subGeometry.id);
  const serverAttachedContactIds = new Set(
    contacts.filter((c) => c.agencySubGeometryId === subGeometry.id).map((c) => c.id),
  );
  const serverAttachedLinkIds = new Set(links.filter((l) => l.agencySubGeometryId === subGeometry.id).map((l) => l.id));

  // Local draft: assignments are staged here and only committed on Save. Seeded once from the
  // server-attached set; create-new and Save fold their results back in (see handlers below).
  const [draftContactIds, setDraftContactIds] = useState<Set<string>>(() => new Set(serverAttachedContactIds));
  const [draftLinkIds, setDraftLinkIds] = useState<Set<string>>(() => new Set(serverAttachedLinkIds));

  const [noteValue, setNoteValue] = useState(subGeometry.notes[0]?.notes ?? '');
  const [saving, setSaving] = useState(false);

  // ---- new-contact inline form state ----
  const [showNewContact, setShowNewContact] = useState(false);
  const contactForm = useForm<TNewContactValues>({
    defaultValues: { firstName: '', lastName: '', email: '', title: '' },
  });

  // ---- new-link inline form state ----
  const [showNewLink, setShowNewLink] = useState(false);
  const linkForm = useForm<TNewLinkValues>({
    defaultValues: { title: '', description: '', url: '', type: AgencyLinkType.Other, isPrivate: false },
  });

  // Seed the note from server truth on open. Keyed on the sub region id (not `notes`) so a post-Save
  // refetch can't clobber an in-progress note edit — e.g. when a member op in the same Save fails.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — see comment above.
  useEffect(() => {
    setNoteValue(subGeometry.notes[0]?.notes ?? '');
  }, [subGeometry.id]);

  // ---- draft mutators (no API call until Save) ----
  const toggleContactDraft = (id: string) =>
    setDraftContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleLinkDraft = (id: string) =>
    setDraftLinkIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // --- Contacts: create stays immediate (per requirement); selecting/removing only stages a draft change ---

  const handleCreateContact: SubmitHandler<TNewContactValues> = async (data) => {
    try {
      const result = await apiNewAgencyContact({
        ...data,
        agency: agencyId,
        agencySubGeometryId: subGeometry.id,
        isDefaultContact: false,
        notes: '',
      });
      capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencySubGeometryMemberAssigned, {
        agency_id: agencyId,
        sub_geometry_id: subGeometry.id,
        member_type: 'contact',
        member_id: result.id,
        entry_point: 'manage_dialog',
      });
      // The new contact is already attached server-side; fold it into the draft so the next Save
      // doesn't read it as a pending removal and detach it.
      setDraftContactIds((prev) => new Set(prev).add(result.id));
      Notify.info({ message: 'Contact created and added to sub region' });
      contactForm.reset();
      setShowNewContact(false);
      onChanged();
    } catch (e) {
      Notify.error({ message: serverReason(e) ?? 'Failed to create contact' });
    }
  };

  // --- Links: create stays immediate; selecting/removing only stages a draft change ---

  const handleCreateLink: SubmitHandler<TNewLinkValues> = async (data) => {
    try {
      const res = await apiAddAgencyLink(agencyId, {
        ...data,
        agencySubGeometryId: subGeometry.id,
      });
      if (!res.id) throw new Error('Bad Response!');
      const newLinkId = res.id;
      capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencySubGeometryMemberAssigned, {
        agency_id: agencyId,
        sub_geometry_id: subGeometry.id,
        member_type: 'link',
        member_id: newLinkId,
        entry_point: 'manage_dialog',
      });
      setDraftLinkIds((prev) => new Set(prev).add(newLinkId));
      Notify.info({ message: 'Link created and added to sub region' });
      linkForm.reset();
      setShowNewLink(false);
      onChanged();
    } catch (e) {
      Notify.error({ message: serverReason(e) ?? 'Failed to create link' });
    }
  };

  // --- Save: commit the staged assignment changes + the note in one action ---

  const contactDiff = computeMemberDiff(serverAttachedContactIds, draftContactIds);
  const linkDiff = computeMemberDiff(serverAttachedLinkIds, draftLinkIds);
  const noteChanged = noteValue !== (subGeometry.notes[0]?.notes ?? '');
  const hasPendingChanges =
    contactDiff.toAttach.length > 0 ||
    contactDiff.toDetach.length > 0 ||
    linkDiff.toAttach.length > 0 ||
    linkDiff.toDetach.length > 0 ||
    noteChanged;

  const handleSave = async () => {
    setSaving(true);
    // Failed items are intentionally left in their pending draft state so Save can be retried; we
    // surface the server's `reason` (e.g. the cross-agency guard from CG-482) when one is present.
    const failureReasons: string[] = [];

    const commitContact = async (contactId: string, assign: boolean) => {
      try {
        await apiUpdateAgencyContact(contactId, { agencySubGeometryId: assign ? subGeometry.id : null });
        capturePosthogEvent(
          assign
            ? POSTHOG_EVENTS.civilsearchAgencySubGeometryMemberAssigned
            : POSTHOG_EVENTS.civilsearchAgencySubGeometryMemberUnassigned,
          {
            agency_id: agencyId,
            sub_geometry_id: subGeometry.id,
            member_type: 'contact',
            member_id: contactId,
            entry_point: 'manage_dialog',
          },
        );
      } catch (e) {
        failureReasons.push(serverReason(e) ?? '');
      }
    };

    const commitLink = async (linkId: string, assign: boolean) => {
      try {
        await apiEditAgencyLink(agencyId, linkId, { agencySubGeometryId: assign ? subGeometry.id : null });
        capturePosthogEvent(
          assign
            ? POSTHOG_EVENTS.civilsearchAgencySubGeometryMemberAssigned
            : POSTHOG_EVENTS.civilsearchAgencySubGeometryMemberUnassigned,
          {
            agency_id: agencyId,
            sub_geometry_id: subGeometry.id,
            member_type: 'link',
            member_id: linkId,
            entry_point: 'manage_dialog',
          },
        );
      } catch (e) {
        failureReasons.push(serverReason(e) ?? '');
      }
    };

    for (const id of contactDiff.toAttach) await commitContact(id, true);
    for (const id of contactDiff.toDetach) await commitContact(id, false);
    for (const id of linkDiff.toAttach) await commitLink(id, true);
    for (const id of linkDiff.toDetach) await commitLink(id, false);

    if (noteChanged) {
      try {
        await apiUpdateAgencySubGeometryNote(agencyId, subGeometry.id, noteValue);
        capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencySubGeometryNoteSaved, {
          agency_id: agencyId,
          sub_geometry_id: subGeometry.id,
          cleared: noteValue.trim() === '',
        });
      } catch (e) {
        failureReasons.push(serverReason(e) ?? '');
      }
    }

    setSaving(false);
    onChanged();
    if (failureReasons.length > 0) {
      const reason = failureReasons.find((r) => r);
      Notify.error({
        message: reason ?? 'Some changes could not be saved. Items still marked pending were not updated — try again.',
      });
    } else {
      Notify.info({ message: 'Sub region saved' });
    }
  };

  // Each list shows the union of server-attached + draft members, so pending adds AND pending
  // removes are both visible (a removal lingers struck-through until Save).
  const displayedContacts = contacts.filter((c) => serverAttachedContactIds.has(c.id) || draftContactIds.has(c.id));
  const displayedLinks = links.filter((l) => serverAttachedLinkIds.has(l.id) || draftLinkIds.has(l.id));

  // Dropdown candidates exclude anything already staged in the draft (those render in the list above).
  const contactCandidateItems = candidateContacts
    .filter((c) => !draftContactIds.has(c.id))
    .map((c) => {
      const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unnamed contact';
      return {
        value: c.id,
        label: c.agencySubGeometryId ? `${name} (assigned to another sub region)` : name,
        keywords: [c.firstName, c.lastName, c.email].filter((k): k is string => Boolean(k)),
      };
    });

  const linkCandidateItems = candidateLinks
    .filter((l) => !draftLinkIds.has(l.id))
    .map((l) => ({
      value: l.id,
      label: l.agencySubGeometryId ? `${l.title} (assigned to another sub region)` : l.title,
      keywords: [l.title, l.type, l.url],
    }));

  return (
    <>
      <DialogBody className="text-sm">
        <div className="flex flex-col gap-6 min-w-[36vw]">
          {/* Contacts section */}
          <section className="flex flex-col gap-2">
            <Text className="text-sm font-semibold">Contacts</Text>

            {displayedContacts.length === 0 ? (
              <Text className="text-xs text-grey-60">No contacts assigned to this sub region.</Text>
            ) : (
              <div className="flex flex-col gap-1">
                {displayedContacts.map((c) => {
                  const state = deriveMemberDraftState(serverAttachedContactIds.has(c.id), draftContactIds.has(c.id));
                  const pendingRemove = state === 'pending-remove';
                  const pendingAdd = state === 'pending-add';
                  const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unnamed contact';
                  return (
                    <div
                      key={c.id}
                      className={classNames('flex items-center justify-between rounded border px-2 py-1', {
                        'border-grey-20': state === 'committed',
                        'border-blue-50': pendingAdd,
                        'border-red-50': pendingRemove,
                      })}
                      data-testid={`agency-sub-geometries-contact-item-${c.id}`}
                    >
                      <div className="flex flex-col">
                        <Text
                          className={classNames('text-xs font-medium', pendingRemove && 'text-grey-50 line-through')}
                        >
                          {name}
                          {c.title ? ` — ${c.title}` : ''}
                        </Text>
                        <Text className={classNames('text-xs text-grey-60', pendingRemove && 'line-through')}>
                          {c.email}
                        </Text>
                        {pendingAdd && (
                          <Text className="text-[10px] font-medium text-blue-50">Pending — not yet saved</Text>
                        )}
                        {pendingRemove && (
                          <Text className="text-[10px] font-medium text-red-50">
                            Marked for removal — not yet saved
                          </Text>
                        )}
                      </div>
                      {pendingRemove ? (
                        <Button
                          variant="secondary"
                          className="text-xs"
                          aria-label={`Restore ${name} to sub region`}
                          data-testid={`agency-sub-geometries-contact-restore-${c.id}`}
                          onClick={() => toggleContactDraft(c.id)}
                        >
                          Undo
                        </Button>
                      ) : (
                        <IconButton
                          icon="cross2"
                          variant="hollow"
                          size="icon"
                          aria-label={`Remove ${name} from sub region`}
                          data-testid={`agency-sub-geometries-contact-detach-${c.id}`}
                          onClick={() => toggleContactDraft(c.id)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {contactCandidateItems.length > 0 && (
              <Select
                label="Add contact"
                items={contactCandidateItems}
                callToAction="Select a contact to add…"
                onChange={(val) => {
                  if (val) toggleContactDraft(String(val));
                }}
                data-testid={`agency-sub-geometries-contact-add-select-${subGeometry.id}`}
              />
            )}

            {/* + New contact toggle */}
            {!showNewContact && (
              <Button
                variant="secondary"
                className="self-start text-xs"
                onClick={() => setShowNewContact(true)}
                data-testid={`agency-sub-geometries-contact-new-toggle-${subGeometry.id}`}
              >
                + New contact
              </Button>
            )}

            {showNewContact && (
              <Form onSubmit={contactForm.handleSubmit(handleCreateContact)}>
                <div
                  className="flex flex-col gap-2 rounded border border-grey-20 p-3"
                  data-testid={`agency-sub-geometries-contact-new-form-${subGeometry.id}`}
                >
                  <Text className="text-xs font-medium text-grey-70">New contact</Text>
                  <ControlledInput
                    control={contactForm.control}
                    name="firstName"
                    label="First name"
                    isRequired={false}
                    tooltipText="Contact first name"
                    placeholder="First name"
                    data-testid={`agency-sub-geometries-contact-new-firstName-${subGeometry.id}`}
                  />
                  <ControlledInput
                    control={contactForm.control}
                    name="lastName"
                    label="Last name"
                    isRequired={false}
                    tooltipText="Contact last name"
                    placeholder="Last name"
                    data-testid={`agency-sub-geometries-contact-new-lastName-${subGeometry.id}`}
                  />
                  <ControlledInput
                    control={contactForm.control}
                    name="email"
                    label="Email"
                    isRequired={false}
                    tooltipText="Contact email address"
                    placeholder="email@example.com"
                    data-testid={`agency-sub-geometries-contact-new-email-${subGeometry.id}`}
                  />
                  <ControlledInput
                    control={contactForm.control}
                    name="title"
                    label="Title"
                    isRequired={false}
                    tooltipText="Contact title"
                    placeholder="e.g. Project Manager"
                    data-testid={`agency-sub-geometries-contact-new-title-${subGeometry.id}`}
                  />
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      disabled={contactForm.formState.isSubmitting}
                      className="self-start text-xs"
                      data-testid={`agency-sub-geometries-contact-new-submit-${subGeometry.id}`}
                    >
                      Create contact
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      className="self-start text-xs"
                      onClick={() => {
                        contactForm.reset();
                        setShowNewContact(false);
                      }}
                      data-testid={`agency-sub-geometries-contact-new-cancel-${subGeometry.id}`}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </section>

          {/* Links section */}
          <section className="flex flex-col gap-2">
            <Text className="text-sm font-semibold">Links</Text>

            {displayedLinks.length === 0 ? (
              <Text className="text-xs text-grey-60">No links assigned to this sub region.</Text>
            ) : (
              <div className="flex flex-col gap-1">
                {displayedLinks.map((l) => {
                  const state = deriveMemberDraftState(serverAttachedLinkIds.has(l.id), draftLinkIds.has(l.id));
                  const pendingRemove = state === 'pending-remove';
                  const pendingAdd = state === 'pending-add';
                  return (
                    <div
                      key={l.id}
                      className={classNames('flex items-center justify-between rounded border px-2 py-1', {
                        'border-grey-20': state === 'committed',
                        'border-blue-50': pendingAdd,
                        'border-red-50': pendingRemove,
                      })}
                      data-testid={`agency-sub-geometries-link-item-${l.id}`}
                    >
                      <div className="flex min-w-0 flex-col">
                        <Text
                          className={classNames('text-xs font-medium', pendingRemove && 'text-grey-50 line-through')}
                        >
                          {l.title} <span className="text-grey-60">({l.type})</span>
                        </Text>
                        <Text className={classNames('truncate text-xs text-grey-60', pendingRemove && 'line-through')}>
                          {l.url}
                        </Text>
                        {pendingAdd && (
                          <Text className="text-[10px] font-medium text-blue-50">Pending — not yet saved</Text>
                        )}
                        {pendingRemove && (
                          <Text className="text-[10px] font-medium text-red-50">
                            Marked for removal — not yet saved
                          </Text>
                        )}
                      </div>
                      {pendingRemove ? (
                        <Button
                          variant="secondary"
                          className="text-xs"
                          aria-label={`Restore ${l.title} to sub region`}
                          data-testid={`agency-sub-geometries-link-restore-${l.id}`}
                          onClick={() => toggleLinkDraft(l.id)}
                        >
                          Undo
                        </Button>
                      ) : (
                        <IconButton
                          icon="cross2"
                          variant="hollow"
                          size="icon"
                          aria-label={`Remove ${l.title} from sub region`}
                          data-testid={`agency-sub-geometries-link-detach-${l.id}`}
                          onClick={() => toggleLinkDraft(l.id)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {linkCandidateItems.length > 0 && (
              <Select
                label="Add link"
                items={linkCandidateItems}
                callToAction="Select a link to add…"
                onChange={(val) => {
                  if (val) toggleLinkDraft(String(val));
                }}
                data-testid={`agency-sub-geometries-link-add-select-${subGeometry.id}`}
              />
            )}

            {/* + New link toggle */}
            {!showNewLink && (
              <Button
                variant="secondary"
                className="self-start text-xs"
                onClick={() => setShowNewLink(true)}
                data-testid={`agency-sub-geometries-link-new-toggle-${subGeometry.id}`}
              >
                + New link
              </Button>
            )}

            {showNewLink && (
              <Form onSubmit={linkForm.handleSubmit(handleCreateLink)}>
                <div
                  className="flex flex-col gap-2 rounded border border-grey-20 p-3"
                  data-testid={`agency-sub-geometries-link-new-form-${subGeometry.id}`}
                >
                  <Text className="text-xs font-medium text-grey-70">New link</Text>
                  <ControlledInput
                    control={linkForm.control}
                    name="title"
                    label="Title"
                    isRequired={false}
                    tooltipText="Link title"
                    placeholder="Link title"
                    data-testid={`agency-sub-geometries-link-new-title-${subGeometry.id}`}
                  />
                  <ControlledInput
                    control={linkForm.control}
                    name="description"
                    label="Description"
                    isRequired={false}
                    tooltipText="Link description"
                    placeholder="Description"
                    data-testid={`agency-sub-geometries-link-new-description-${subGeometry.id}`}
                  />
                  <ControlledInput
                    control={linkForm.control}
                    name="url"
                    label="URL"
                    isRequired={false}
                    tooltipText="Link URL"
                    placeholder="https://..."
                    data-testid={`agency-sub-geometries-link-new-url-${subGeometry.id}`}
                  />
                  <ControlledSelect
                    control={linkForm.control}
                    name="type"
                    label="Link type"
                    isRequired
                    tooltipText="Select the type for this link"
                    items={linkTypeItems}
                    data-testid={`agency-sub-geometries-link-new-type-${subGeometry.id}`}
                  />
                  <ControlledCheckbox
                    control={linkForm.control}
                    name="isPrivate"
                    label="Private — not visible to users"
                    data-testid={`agency-sub-geometries-link-new-isPrivate-${subGeometry.id}`}
                  />
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      disabled={linkForm.formState.isSubmitting}
                      className="self-start text-xs"
                      data-testid={`agency-sub-geometries-link-new-submit-${subGeometry.id}`}
                    >
                      Create link
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      className="self-start text-xs"
                      onClick={() => {
                        linkForm.reset();
                        setShowNewLink(false);
                      }}
                      data-testid={`agency-sub-geometries-link-new-cancel-${subGeometry.id}`}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </section>

          {/* Notes section */}
          <section className="flex flex-col gap-2">
            <Text className="text-sm font-semibold">Note</Text>
            <textarea
              className="w-full rounded border border-grey-30 p-2 text-xs leading-relaxed text-grey-80 focus:border-focus focus:outline-none"
              rows={4}
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
              placeholder="Add a note for this sub region…"
              data-testid={`agency-sub-geometries-notes-textarea-${subGeometry.id}`}
            />
          </section>
        </div>
      </DialogBody>

      <DialogFooter>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={saving}
          data-testid={`agency-sub-geometries-manage-cancel-${subGeometry.id}`}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !hasPendingChanges}
          data-testid={`agency-sub-geometries-manage-save-${subGeometry.id}`}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </>
  );
}
